// PIVOT-clause parsing + post-PIVOT shape transform.
//
// A PIVOT clause `PIVOT ( <agg>(<col>) FOR <col> IN (<v> AS <alias>, …) )`
// rewrites the row shape of the relation it follows: the FOR column and the
// aggregate-argument column are CONSUMED, and one new column per `IN`-list
// value alias is produced. The autocomplete resolver needs this so that, at
// a post-PIVOT scope (`… ) p WHERE p.|`), it offers the pivoted shape rather
// than the pre-pivot projection.
//
// Engine-agnostic — PIVOT syntax is shared by Oracle and SQL Server. This is
// heuristic-quality (not a full grammar): it covers the single-aggregate /
// single-FOR-column / value-alias forms users actually write, and the
// multi-column FOR tuple form. Anonymous/expression value items fall back to
// the literal text as the column name (mirrors the engine's own naming).

export type ParsedPivotClause = {
  /** Columns named in the `FOR (…)` slot — consumed by the pivot. */
  readonly forColumns: readonly string[];
  /** Bare column arguments of the aggregate function(s) — consumed. */
  readonly aggregateArgColumns: readonly string[];
  /** Output alias of each aggregate (`SUM(x) AS s` → `s`). Empty for a
   *  single unnamed aggregate, the common case (`SUM(x)` / `COUNT(*)`). */
  readonly aggregateAliases: readonly string[];
  /** New column names produced, one per `IN`-list value. */
  readonly valueAliases: readonly string[];
};

type Lexeme = {
  readonly text: string;
  readonly type: 'ident' | 'num' | 'str' | 'punct';
  /** Paren depth this token sits at (lparen reported at its OUTER depth). */
  readonly depth: number;
};

const WORD_START = /[A-Za-z_$#@]/;
const WORD_PART = /[A-Za-z0-9_$#]/;
const DIGIT = /[0-9]/;
const SKIP_AGG_WORDS = new Set(['AS', 'DISTINCT', 'ALL', 'UNIQUE']);

export function parsePivotClause(raw: string): ParsedPivotClause | null {
  const toks = lex(stripWrap(raw));
  const forIdx = toks.findIndex(
    (t) => t.type === 'ident' && t.depth === 0 && t.text.toUpperCase() === 'FOR',
  );
  if (forIdx === -1) return null;
  const inIdx = toks.findIndex(
    (t, i) => i > forIdx && t.type === 'ident' && t.depth === 0 && t.text.toUpperCase() === 'IN',
  );
  if (inIdx === -1) return null;

  const aggSection = toks.slice(0, forIdx);
  const aggregateArgColumns = collectColumns(aggSection);
  const aggregateAliases = collectAggregateAliases(aggSection);
  const forColumns = collectColumns(toks.slice(forIdx + 1, inIdx));
  const valueAliases = collectValueAliases(toks.slice(inIdx + 1));
  return { forColumns, aggregateArgColumns, aggregateAliases, valueAliases };
}

/**
 * Apply the pivot to a pre-pivot column list: drop the FOR / aggregate-arg
 * columns (case-insensitive), then append the value aliases. Survivors keep
 * their original casing; a value alias that collides with a survivor is not
 * duplicated.
 */
export function applyPivotTransform(
  baseColumns: readonly string[],
  parsed: ParsedPivotClause,
): string[] {
  const drop = new Set(
    [
      ...parsed.forColumns,
      ...parsed.aggregateArgColumns,
      ...parsed.aggregateAliases,
    ].map((c) => c.toLowerCase()),
  );
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (name: string) => {
    const folded = name.toLowerCase();
    if (seen.has(folded)) return;
    seen.add(folded);
    out.push(name);
  };
  for (const c of baseColumns) {
    if (drop.has(c.toLowerCase())) continue;
    push(c);
  }
  // Column naming: a single UNNAMED aggregate produces one column per value
  // alias (`y20`); NAMED aggregate(s) produce the `value_aggalias` cross
  // product Oracle/SQL Server generate (`y20_total`, `y20_s` + `y20_c`).
  if (parsed.aggregateAliases.length === 0) {
    for (const v of parsed.valueAliases) push(v);
  } else {
    for (const v of parsed.valueAliases) {
      for (const a of parsed.aggregateAliases) push(`${v}_${a}`);
    }
  }
  return out;
}

// ───────────────────────────────────────────────────────────────────────────
// Internals
// ───────────────────────────────────────────────────────────────────────────

/** Strip a wrapping pair of T-SQL identifier brackets (`[yr]` → `yr`). */
function unbracket(s: string): string {
  return s.length >= 2 && s[0] === '[' && s[s.length - 1] === ']'
    ? s.slice(1, -1)
    : s;
}

/** Collect bare/dotted column identifiers, skipping function names + keywords. */
function collectColumns(toks: readonly Lexeme[]): string[] {
  const out: string[] = [];
  for (let k = 0; k < toks.length; k += 1) {
    const tok = toks[k];
    if (tok.type !== 'ident') continue;
    if (SKIP_AGG_WORDS.has(tok.text.toUpperCase())) continue;
    const next = toks[k + 1];
    if (next && next.type === 'punct' && next.text === '(') continue; // function name
    if (next && next.type === 'punct' && next.text === '.') continue; // qualifier prefix
    // Drop-set matching is against the pre-pivot projection (unbracketed
    // column names), so normalise `[yr]` → `yr` here. Value-list names keep
    // their brackets (handled in `collectValueAliases`).
    out.push(unbracket(tok.text));
  }
  return out;
}

/** Output alias of each comma-separated aggregate (`SUM(x) AS s` → `s`,
 *  `COUNT(*) c` → `c`). A single unnamed aggregate (`SUM(x)`) yields []. */
function collectAggregateAliases(toks: readonly Lexeme[]): string[] {
  const exprs: Lexeme[][] = [[]];
  for (const tok of toks) {
    if (tok.depth === 0 && tok.type === 'punct' && tok.text === ',') {
      exprs.push([]);
      continue;
    }
    exprs[exprs.length - 1].push(tok);
  }
  const out: string[] = [];
  for (const expr of exprs) {
    if (expr.length === 0) continue;
    const asIdx = expr.findIndex(
      (t) => t.type === 'ident' && t.text.toUpperCase() === 'AS',
    );
    if (asIdx !== -1 && expr[asIdx + 1]?.type === 'ident') {
      out.push(expr[asIdx + 1].text);
      continue;
    }
    // Implicit alias — a trailing identifier sitting after the aggregate's
    // closing paren (depth 0), e.g. `COUNT(*) c`.
    const last = expr[expr.length - 1];
    if (last && last.depth === 0 && last.type === 'ident') {
      out.push(last.text);
    }
  }
  return out;
}

/** Split the `IN ( … )` list into items and resolve each item's column name. */
function collectValueAliases(afterIn: readonly Lexeme[]): string[] {
  // The first token should be the `(` opening the value list (depth 0); its
  // items sit at depth 1.
  const open = afterIn.findIndex((t) => t.type === 'punct' && t.text === '(');
  if (open === -1) return [];
  const items: Lexeme[][] = [[]];
  for (let k = open + 1; k < afterIn.length; k += 1) {
    const tok = afterIn[k];
    if (tok.depth === 0 && tok.type === 'punct' && tok.text === ')') break; // list closed
    if (tok.depth === 1 && tok.type === 'punct' && tok.text === ',') {
      items.push([]);
      continue;
    }
    items[items.length - 1].push(tok);
  }
  const out: string[] = [];
  for (const item of items) {
    const name = valueItemName(item);
    if (name) out.push(name);
  }
  return out;
}

function valueItemName(item: readonly Lexeme[]): string | null {
  const asIdx = item.findIndex((t) => t.type === 'ident' && t.text.toUpperCase() === 'AS');
  if (asIdx !== -1 && item[asIdx + 1]) {
    return item[asIdx + 1].text;
  }
  // Implicit alias — a trailing identifier after the value expression.
  const last = item[item.length - 1];
  if (item.length >= 2 && last && last.type === 'ident') {
    return last.text;
  }
  // Bare value — use its literal text as the column name.
  if (item.length >= 1 && item[0] && (item[0].type === 'str' || item[0].type === 'num')) {
    return item[0].text;
  }
  if (item.length === 1 && item[0] && item[0].type === 'ident') {
    return item[0].text;
  }
  return null;
}

/** Strip one or more fully-wrapping outer paren pairs. */
function stripWrap(s: string): string {
  let t = s.trim();
  while (t.length >= 2 && t[0] === '(' && t[t.length - 1] === ')' && wrapsWhole(t)) {
    t = t.slice(1, -1).trim();
  }
  return t;
}

function wrapsWhole(t: string): boolean {
  let depth = 0;
  for (let i = 0; i < t.length; i += 1) {
    const c = t[i];
    if (c === "'" || c === '"') {
      const q = c;
      i += 1;
      while (i < t.length) {
        if (t[i] === q) {
          if (t[i + 1] === q) {
            i += 1;
          } else {
            break;
          }
        }
        i += 1;
      }
      continue;
    }
    if (c === '(') {
      depth += 1;
    } else if (c === ')') {
      depth -= 1;
      if (depth === 0 && i !== t.length - 1) return false;
    }
  }
  return depth === 0;
}

function lex(s: string): Lexeme[] {
  const out: Lexeme[] = [];
  let i = 0;
  let depth = 0;
  while (i < s.length) {
    const ch = s[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i += 1;
      continue;
    }
    if (ch === '(') {
      out.push({ text: '(', type: 'punct', depth });
      depth += 1;
      i += 1;
      continue;
    }
    if (ch === ')') {
      depth -= 1;
      out.push({ text: ')', type: 'punct', depth });
      i += 1;
      continue;
    }
    if (ch === "'" || ch === '"') {
      const q = ch;
      let j = i + 1;
      let val = '';
      while (j < s.length) {
        if (s[j] === q) {
          if (s[j + 1] === q) {
            val += q;
            j += 2;
            continue;
          }
          break;
        }
        val += s[j];
        j += 1;
      }
      out.push({ text: val, type: 'str', depth });
      i = j + 1;
      continue;
    }
    if (ch === '[') {
      // T-SQL bracketed identifier (`[2011]`, `[yr]`). Read to the closing
      // `]` and keep it as one `ident` token, brackets preserved — value-list
      // names like `[2011]` are emitted bracketed (the engine's column name),
      // while `collectColumns` strips the brackets for drop-set matching.
      let j = i + 1;
      while (j < s.length && s[j] !== ']') j += 1;
      out.push({ text: s.slice(i, j + 1), type: 'ident', depth });
      i = j + 1;
      continue;
    }
    if (DIGIT.test(ch)) {
      let j = i;
      while (j < s.length && (DIGIT.test(s[j]) || s[j] === '.')) j += 1;
      out.push({ text: s.slice(i, j), type: 'num', depth });
      i = j;
      continue;
    }
    if (WORD_START.test(ch)) {
      let j = i;
      while (j < s.length && WORD_PART.test(s[j])) j += 1;
      out.push({ text: s.slice(i, j), type: 'ident', depth });
      i = j;
      continue;
    }
    out.push({ text: ch, type: 'punct', depth });
    i += 1;
  }
  return out;
}
